/**
 * @file 路由视图状态工具
 * @description 控制 query 更新时是否重建页面组件、刷新 noCache 版本和重置滚动位置。
 */
import type { RouteMeta } from 'vue-router'

/** 路由视图状态判断所需的最小路由信息。 */
export interface RouteViewLocation {
  /** 不含 query 和 hash 的实际路径。 */
  path: string
  /** 包含 query 和 hash 的完整路径。 */
  fullPath: string
  /** 路由元信息。 */
  meta: RouteMeta
}

/**
 * 生成路由页面组件 key。
 * @param route 当前路由。
 * @param refreshVersion noCache 路由的手动刷新版本。
 * @returns 用于 Vue 动态组件的稳定 key。
 */
export function buildRouteViewKey(
  route: RouteViewLocation,
  refreshVersion: number
): string {
  const routeIdentity = route.meta.preserveOnQuery ? route.path : route.fullPath
  return route.meta.noCache
    ? `${routeIdentity}-${refreshVersion}`
    : routeIdentity
}

/**
 * 判断本次导航是否需要更新 noCache 路由刷新版本。
 * @param to 目标路由。
 * @param from 来源路由。
 * @returns query-only 导航且要求保留页面实例时返回 false。
 */
export function shouldTouchRouteRefresh(
  to: RouteViewLocation,
  from: RouteViewLocation
): boolean {
  if (!to.meta.noCache) return false
  return !to.meta.preserveOnQuery || to.path !== from.path
}

/**
 * 判断导航完成后是否需要把主滚动容器移到顶部。
 * @param to 目标路由。
 * @param from 来源路由。
 * @returns query-only 导航且要求保留页面状态时返回 false。
 */
export function shouldResetLayoutScroll(
  to: RouteViewLocation,
  from: RouteViewLocation
): boolean {
  return !to.meta.preserveOnQuery || to.path !== from.path
}
